'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useMemo } from 'react';
import Clock from '@crehana/compass.icons.outline/Clock';
import { cn } from "../utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Select";
import { HOUR_ARRAY } from "./constants";
import { calculateTotalMinutes, generateMinuteArray, getHourData } from "./utils";

/**
 * Select variant — renders a flat Radix Select dropdown with all time options.
 *
 * Each option's `value` is in 24h "HH:mm" format so it round-trips cleanly
 * with the parent's value/onTimeChange contract. The display label is formatted
 * per mode (e.g. "09:30 a.m" in a.m/p.m mode, "09:30" in 24hour mode).
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export var TimePickerSelect = function TimePickerSelect(_ref) {
  var className = _ref.className,
    value = _ref.value,
    _ref$mode = _ref.mode,
    mode = _ref$mode === void 0 ? 'a.m/p.m' : _ref$mode,
    placeholder = _ref.placeholder,
    onTimeChange = _ref.onTimeChange,
    _ref$minuteInterval = _ref.minuteInterval,
    minuteInterval = _ref$minuteInterval === void 0 ? 15 : _ref$minuteInterval,
    disabled = _ref.disabled,
    removePortal = _ref.removePortal,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 's' : _ref$size,
    error = _ref.error,
    helperText = _ref.helperText;
  // Pre-compute all time options as { value (24h), displayLabel, displaySuffix }.
  // HOUR_ARRAY is 00-23 in 24h format; getHourData converts each hour for display.
  var options = useMemo(function () {
    var minuteArr = generateMinuteArray(minuteInterval);
    var result = [];
    HOUR_ARRAY.forEach(function (hour) {
      minuteArr.forEach(function (minute) {
        var _getHourData = getHourData(hour, mode),
          hourStr = _getHourData.hourStr,
          suffix = _getHourData.suffix;
        result.push({
          // 24h format as the canonical value — matches what onTimeChange emits
          value: "".concat(hour, ":").concat(minute),
          // Display-only: 12h hour in a.m/p.m mode, 24h hour otherwise
          displayLabel: "".concat(hourStr, ":").concat(minute),
          displaySuffix: suffix
        });
      });
    });
    return result;
  }, [minuteInterval, mode]);
  var handleValueChange = useCallback(function (newValue) {
    // newValue is already 24h "HH:mm" — split and compute totalMinutes for the callback.
    var _newValue$split$map = newValue.split(':').map(Number),
      _newValue$split$map2 = _slicedToArray(_newValue$split$map, 2),
      h = _newValue$split$map2[0],
      m = _newValue$split$map2[1];
    onTimeChange === null || onTimeChange === void 0 ? void 0 : onTimeChange(newValue, calculateTotalMinutes(h, m));
  }, [onTimeChange]);
  return /*#__PURE__*/_jsxs("div", {
    className: "tw-flex tw-flex-col tw-gap-0.8",
    children: [/*#__PURE__*/_jsxs(Select, {
      value: value,
      onValueChange: handleValueChange,
      disabled: disabled,
      children: [/*#__PURE__*/_jsx(SelectTrigger, {
        className: cn(size === 'm' && 'tw-h-5.6 tw-font-body3 tw-text-1.6',
        // Error border persists across all interactive states
        error && 'tw-border-alert-400 hover:tw-border-alert-400 focus-within:tw-border-alert-400 data-[state=open]:tw-border-alert-400', className),
        "aria-invalid": error || undefined,
        renderIcon: function renderIcon() {
          return /*#__PURE__*/_jsx(Clock, {
            className: cn(error ? 'tw-text-alert-400' : 'tw-text-neutral-light-600'),
            size: 24
          });
        },
        children: /*#__PURE__*/_jsx(SelectValue, {
          placeholder: /*#__PURE__*/_jsx("span", {
            className: "tw-text-neutral-light-700",
            children: placeholder
          })
        })
      }), /*#__PURE__*/_jsx(SelectContent, {
        removePortal: removePortal,
        children: options.map(function (option) {
          return /*#__PURE__*/_jsxs(SelectItem, {
            value: option.value,
            children: [/*#__PURE__*/_jsx("span", {
              className: "tw-inline-block tw-w-4.4",
              children: option.displayLabel
            }), /*#__PURE__*/_jsx("span", {
              children: option.displaySuffix
            })]
          }, option.value);
        })
      })]
    }), error && helperText && /*#__PURE__*/_jsx("p", {
      className: "tw-font-caption tw-text-alert-400",
      children: helperText
    })]
  });
};